import {
  Box,
  Grid,
  GridColumn,
  Icon,
  Target,
  Text,
  useOverlay,
  useThemeWithoutTypes
} from "@modernatx-internal/component-library";
import React from "react";
import ReactDOM from "react-dom";

import useBodyScrollLock from "@/content-V2/hooks/useBodyScrollLock";
import { ContentComponentProps, ISIPanelContent } from "@/content-V2/types";
import { useExperience } from "@/context/ExperienceContext";
import { useClickOutside } from "@/hooks/useClickOutside";

import { useIsMobile } from "../hooks/useIsMobile";

export type Expanded = "preview" | boolean;

const LEGACY_COLUMN_SIZES = {
  textColumn1: [4, 2, 4] as NonNullable<React.ComponentProps<typeof GridColumn>["size"]>,
  textColumn2: [4, 4, 8] as NonNullable<React.ComponentProps<typeof GridColumn>["size"]>
};

const UNIFORM_COLUMN_SIZES = {
  textColumn1: [12, 4, 4] as NonNullable<React.ComponentProps<typeof GridColumn>["size"]>,
  textColumn2: [12, 8, 8] as NonNullable<React.ComponentProps<typeof GridColumn>["size"]>
};

const isUniformGridTheme = (theme: ReturnType<typeof useThemeWithoutTypes>) =>
  theme?.grid?.gridMobileColumnCount === 12 &&
  theme?.grid?.gridTabletColumnCount === 12 &&
  theme?.grid?.gridDesktopColumnCount === 12;

const ISIPanelFixed: React.FC<ContentComponentProps<ISIPanelContent> & { hidden: boolean }> = ({
  hidden,
  textColumn1,
  textColumn2,
  title,
  toggleBackgroundColor = "color-current-brand-700"
}) => {
  const { getOverlayVisible } = useOverlay();
  const { screenshotFullPage } = useExperience();
  const panelContentRef = React.useRef<HTMLDivElement>(null);
  const windowHeightRef = React.useRef<number | null>(null);
  const toggleRef = React.useRef<HTMLElement>(null);
  const [toggleHeight, toggleHeightSet] = React.useState(0);
  const [expanded, setExpanded] = React.useState<Expanded>(false);
  const [transitioning, setTransitioning] = React.useState(false);
  const icon: React.ComponentProps<typeof Icon>["icon"] = !!expanded ? "caret-down" : "caret-up";
  const isMobile = useIsMobile();
  const theme = useThemeWithoutTypes();
  const columnSizes = isUniformGridTheme(theme) ? UNIFORM_COLUMN_SIZES : LEGACY_COLUMN_SIZES;

  const transform = React.useMemo(() => {
    const peekHeight = isMobile ? 300 : 180;

    if (expanded === "preview" && toggleHeight) {
      // Just a peek so that 3 lines of text are showing
      return `translateY(calc(100% - ${toggleHeight + peekHeight}px))`;
    } else if (expanded === true) {
      return "translateY(0)";
    } else if (hidden) {
      return "translateY(calc(100% + 90px))";
    } else if (expanded === false) {
      return `translateY(calc(100% - ${toggleHeight}px))`;
    }
  }, [expanded, hidden, isMobile, toggleHeight]);

  const handleExpandedChange = React.useCallback((expandedNext: Expanded) => {
    // We need to flag that the transition is starting
    // so we don't prematurely hide the content
    setTransitioning(true);
    setExpanded(expandedNext);
  }, []);

  useClickOutside(
    panelContentRef,
    React.useCallback(() => {
      if (expanded && !getOverlayVisible()) {
        handleExpandedChange(false);
      }
    }, [expanded, getOverlayVisible, handleExpandedChange])
  );

  const handleToggleClick = React.useCallback(() => {
    if (expanded === "preview") {
      handleExpandedChange(false);
    } else {
      handleExpandedChange(!expanded);
    }
  }, [expanded, handleExpandedChange]);

  const handleTransitionEnd = React.useCallback((e: React.TransitionEvent) => {
    if (e.target === e.currentTarget) {
      setTransitioning(false);
    }
  }, []);

  const handleResize = React.useCallback(() => {
    if (toggleRef.current) {
      windowHeightRef.current = window.innerHeight;
      const toggleHeightNext = toggleRef.current?.offsetHeight || 0;

      if (toggleHeightNext !== toggleHeight) {
        toggleHeightSet(toggleHeightNext);
      }
    }
  }, [toggleHeight]);

  // Cache the window height to reduce DOM lookups and relayouts
  React.useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize, windowHeightRef]);

  React.useEffect(() => {
    handleResize();
  });

  useBodyScrollLock(expanded, panelContentRef);

  React.useEffect(() => {
    handleExpandedChange("preview");
  }, [handleExpandedChange]);

  // If we are previewing, we should collapse the panel
  // for any interaction. Clicks will be taken care of using the
  // useClickOutside hook above.
  React.useEffect(() => {
    const collapseOnAnyInteraction = () => {
      handleExpandedChange(false);
    };

    if (expanded === "preview") {
      window.addEventListener("scroll", collapseOnAnyInteraction);
    }

    return () => {
      window.removeEventListener("scroll", collapseOnAnyInteraction);
    };
  }, [expanded, handleExpandedChange]);

  return (
    <Box
      ref={panelContentRef}
      aria-label="Panel overlay"
      data-cookie-modal-anchor="true"
      data-screenshot-hide={screenshotFullPage ? true : undefined}
      role="dialog"
      sx={{
        backgroundColor: "surface-neutral-secondary",
        bottom: 0,
        maxHeight: "100%",
        position: "fixed",
        transform: transform,
        transition: "transition-ease-out-medium",
        width: "100%",
        zIndex: ({ zIndices }) => zIndices.panel - 1
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%"
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        <Target
          data-testid="isi-panel-toggle"
          sx={{ display: "block", width: "100%", flexGrow: 0 }}
          onClick={handleToggleClick}
        >
          <Box ref={toggleRef} sx={{ backgroundColor: toggleBackgroundColor, py: [2, 4, 4] }}>
            <Grid>
              <GridColumn
                sx={{
                  alignItems: "center",
                  display: ["flex", "none", "none"],
                  flexDirection: "row"
                }}
              >
                <Text as="h3" sx={{ color: "white" }} variant="title-2-medium">
                  {title}
                </Text>
                <Icon color="white" size="md" icon={icon} sx={{ marginInlineStart: "auto" }} />
              </GridColumn>
            </Grid>
            <Box sx={{ display: ["none", "flex", "flex"] }}>
              <Grid>
                <GridColumn sx={{ alignItems: "center", flexDirection: "row" }}>
                  <Text as="h3" sx={{ color: "white" }} variant="h6">
                    {title}
                  </Text>
                  <Icon size="md" icon={icon} sx={{ color: "white", marginInlineStart: "auto" }} />
                </GridColumn>
              </Grid>
            </Box>
          </Box>
        </Target>
        <Box
          sx={{
            backgroundColor: "surface-neutral-secondary",
            flex: 1,
            // We have to manually create the height for Safari. The flex definition
            // covers it for every other browser.
            maxHeight: () =>
              toggleRef.current && expanded !== "preview"
                ? `${(windowHeightRef.current || 0) - toggleRef.current.offsetHeight}px`
                : null,
            overflow: "auto",
            visibility: !expanded && !transitioning ? "hidden" : null
          }}
          aria-hidden={!expanded}
        >
          <Box sx={{ pb: [2, 10, 10], pt: [2, 5, 5] }}>
            <Grid>
              <GridColumn size={columnSizes.textColumn1}>{textColumn1}</GridColumn>
              <GridColumn size={columnSizes.textColumn2}>{textColumn2}</GridColumn>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const ISIPanel: React.FC<ContentComponentProps<ISIPanelContent>> = (props) => {
  const { textColumn1, textColumn2, title } = props;
  const contentRef = React.useRef<HTMLDivElement>(null);
  const portalRef = React.useRef<Element | null>(null);
  const windowHeightRef = React.useRef<number | null>(null);
  const [, setForceUpdate] = React.useState(0);
  const [hidden, setHidden] = React.useState(true);

  React.useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const isContentInView =
          window.scrollY + (windowHeightRef.current || 0) >= contentRef.current.offsetTop;

        if ((isContentInView && !hidden) || (!isContentInView && hidden)) {
          setHidden(isContentInView);
        }
      }
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [hidden, windowHeightRef]);

  React.useEffect(() => {
    const handleResize = () => {
      windowHeightRef.current = window.innerHeight;
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [hidden, windowHeightRef]);

  React.useEffect(() => {
    // Force a render on mount so the element exists and
    // transitions start working
    setForceUpdate(1);
  }, []);

  React.useEffect(() => {
    portalRef.current = document.createElement("div");

    if (portalRef.current) {
      document.body.appendChild(portalRef.current);
    }

    return () => {
      if (portalRef.current) {
        document.body.removeChild(portalRef.current);
      }
    };
  }, []);

  return (
    <>
      {portalRef.current &&
        ReactDOM.createPortal(<ISIPanelFixed {...props} hidden={hidden} />, portalRef.current)}
      <Box
        ref={contentRef}
        sx={{
          backgroundColor: "surface-neutral-secondary",
          py: [7, null, 11]
        }}
      >
        <Grid>
          <GridColumn>
            <Text as="h3" sx={{ pb: 5 }} variant="h5">
              {title}
            </Text>
          </GridColumn>
          <GridColumn>
            <Box
              sx={{
                columnCount: [1, 1, 2],
                gap: (theme) => `${theme.sizes.gridDesktopColumnGutter}px`,
                orphans: 3,
                widows: 3,
                "& h3, & h4": {
                  breakAfter: "avoid"
                }
              }}
            >
              <>{textColumn1}</>
              <>{textColumn2}</>
            </Box>
          </GridColumn>
        </Grid>
      </Box>
    </>
  );
};
